import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { Collapse, IconButton, TextField, Grid, Autocomplete, CircularProgress, Typography } from '@mui/material';
import { INGESTION_STATUS } from '../../services/config/ApiConstants';
import { useEquipementWizardHook } from './EquipementWizardHook';
import FamilleEquipements from './steps/FamilleEquipements';
import GestionEquipements from './steps/GestionEquipements';

const EquipementWizard = ({ context }) => {
  const { t } = useTranslation();
  const [openFamille, setOpenFamille] = useState(true);
  const [openDefaillance, setOpenDefaillance] = useState(true);
  const [openGestion, setOpenGestion] = useState(true);
  const {
    status,
    agingLaws,
    addTechVar,
    familleEquipement,
    scenarioId,
    datasetId,
    doesDatasetExist,
    isDatasetTwingraph,
    datasetIngestionStatus,
  } = useEquipementWizardHook();

  const familleEquipementList = useMemo(() => {
    return agingLaws?.FamilleEquipement?.map((fe) => fe.Identifiant) ?? [];
  }, [agingLaws]);
  const sortedFamilleEquipementList = useMemo(() => {
    return [...(familleEquipementList ?? [])].sort((a, b) => a.localeCompare(b));
  }, [familleEquipementList]);
  if (!datasetId)
    return (
      <Grid container sx={{ justifyContent: 'center', alignItems: 'center' }}>
        <Typography>
          {t(
            'commoncomponents.equipment.datasetErrors.noDatasetAssociated',
            'No dataset is associated with the chosen scenario'
          )}
        </Typography>
      </Grid>
    );
  if (!doesDatasetExist)
    return (
      <Grid container sx={{ justifyContent: 'center', alignItems: 'center' }}>
        <Typography>
          {t(
            'commoncomponents.equipment.datasetErrors.datasetNotFound',
            'The dataset associated with the scenario could not be found'
          )}
        </Typography>
      </Grid>
    );
  if (!isDatasetTwingraph)
    return (
      <Grid container sx={{ justifyContent: 'center', alignItems: 'center' }}>
        <Typography>
          {t(
            'commoncomponents.equipment.datasetErrors.datasetNotTwingraph',
            'The dataset associated with the scenario is not of type twingraph'
          )}
        </Typography>
      </Grid>
    );
  if (datasetIngestionStatus !== INGESTION_STATUS.SUCCESS)
    return (
      <Grid container sx={{ justifyContent: 'center', alignItems: 'center' }}>
        <Typography>
          {t(
            'commoncomponents.equipment.datasetErrors.datasetNotReady',
            'The dataset associated with the scenario is not ready, its status is {{status}}',
            { status: datasetIngestionStatus }
          )}
        </Typography>
      </Grid>
    );
  return status === 'LOADING' ? (
    <CircularProgress data-cy="aging-laws-queries-loading-spinner" />
  ) : (
    <div style={{ padding: '20px' }}>
      <Grid container spacing={2} style={{ marginBottom: '20px' }}>
        <Grid item xs={12} sm={6}>
          <Autocomplete
            id="equipment-family"
            options={sortedFamilleEquipementList}
            value={sortedFamilleEquipementList?.find((option) => option === familleEquipement) || null}
            onChange={(event, newValue) => {
              addTechVar({ familleEquipement: newValue }, scenarioId);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('commoncomponents.equipment.wizard.familySelector.label', 'Equipment family')}
                fullWidth
                size="small"
              />
            )}
          />
        </Grid>
      </Grid>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h3>{t('commoncomponents.equipment.wizard.sections.managementTitle', 'Management of equipment families')}</h3>
        <IconButton onClick={() => setOpenFamille(!openFamille)}>
          {openFamille ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </div>
      <Collapse in={openFamille} sx={{ width: '100%', height: 'max-content' }}>
        <FamilleEquipements context={context} />
      </Collapse>

      <hr style={{ margin: '20px 0' }} />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h3>{t('commoncomponents.equipment.wizard.sections.failureParameters', 'Failure parameters')}</h3>
        <IconButton onClick={() => setOpenDefaillance(!openDefaillance)}>
          {openDefaillance ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </div>
      <Collapse in={openDefaillance}>
        <FamilleEquipements context={context} mode="defaillance" />
      </Collapse>

      <hr style={{ margin: '20px 0' }} />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h3>{t('commoncomponents.equipment.wizard.sections.agePyramid', 'Age pyramid')}</h3>
        <IconButton onClick={() => setOpenGestion(!openGestion)}>
          {openGestion ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </div>
      <Collapse in={openGestion}>{<GestionEquipements />}</Collapse>
    </div>
  );
};

EquipementWizard.propTypes = {
  context: PropTypes.object,
};

export default EquipementWizard;
